import React from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';

const SummaryStep = ({ formData, updateFormData, onEdit }) => {
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      weekday: 'long', 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  const formatTime = (timeString) => {
    if (!timeString) return '';
    const [hours, minutes] = timeString.split(':');
    const date = new Date();
    date.setHours(parseInt(hours), parseInt(minutes));
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const getOccasionTypeLabel = () => {
    if (formData.customOccasionType) {
      return formData.customOccasionType;
    }
    
    const occasionTypes = {
      'birthday': 'Birthday',
      'anniversary': 'Anniversary',
      'wedding': 'Wedding',
      'graduation': 'Graduation',
      'baby_shower': 'Baby Shower',
      'housewarming': 'Housewarming',
      'holiday': 'Holiday',
      'retirement': 'Retirement',
      'promotion': 'Promotion',
      'thank_you': 'Thank You',
      'get_well': 'Get Well'
    };
    
    return occasionTypes[formData.occasionType] || formData.occasionType;
  };

  const getRelationshipLabel = () => {
    const relationships = {
      'family': 'Family',
      'friend': 'Friend',
      'colleague': 'Colleague',
      'partner': 'Partner',
      'acquaintance': 'Acquaintance',
      'mentor': 'Mentor',
      'client': 'Client',
      'neighbor': 'Neighbor'
    };
    
    return relationships[formData.relationship] || formData.relationship;
  };

  const getSignificanceLabel = () => {
    const significanceLevels = {
      'casual': 'Casual',
      'important': 'Important',
      'milestone': 'Milestone',
      'once_in_lifetime': 'Once in a Lifetime'
    };
    
    return significanceLevels[formData.significance] || formData.significance;
  };

  const getBudgetLabel = () => {
    const budgetRanges = {
      'under_25': 'Under $25',
      '25_50': '$25 - $50',
      '50_100': '$50 - $100',
      '100_200': '$100 - $200',
      '200_500': '$200 - $500',
      'over_500': 'Over $500',
      'no_limit': 'No specific limit'
    };
    
    return budgetRanges[formData.budgetRange] || formData.budgetRange;
  };

  const getReminderText = () => {
    if (!formData.reminderSettings.enabled) return 'Disabled';
    
    const days = formData.reminderSettings.days.join(', ');
    const time = formatTime(formData.reminderSettings.times[0]);
    
    return `${days} day${formData.reminderSettings.days.length > 1 ? 's' : ''} before at ${time}`;
  };

  return (
    <div className="space-y-8">
      {/* Step Title */}
      <div className="text-center">
        <h2 className="font-heading font-semibold text-2xl text-accent-800 mb-2">
          Review & Confirm
        </h2>
        <p className="text-text-secondary">
          Please review your occasion details before creating
        </p>
      </div>

      {/* Summary Card */}
      <div className="bg-gradient-to-br from-primary-50 to-secondary-50 rounded-2xl p-6 border border-primary-200">
        <div className="flex items-center space-x-4 mb-6">
          <div className="w-16 h-16 bg-gradient-to-br from-primary to-secondary rounded-2xl flex items-center justify-center">
            <Icon name="Calendar" size={24} color="white" />
          </div>
          <div>
            <h3 className="font-heading font-semibold text-xl text-accent-800">
              {getOccasionTypeLabel()}
            </h3>
            <p className="text-text-secondary font-medium">
              For {formData.recipient}
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Date & Time */}
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Icon name="Calendar" size={18} className="text-primary mt-1" />
              <div>
                <p className="font-medium text-accent-800">Date</p>
                <p className="text-text-secondary">{formatDate(formData.date)}</p>
              </div>
            </div>

            {formData.time && (
              <div className="flex items-start space-x-3">
                <Icon name="Clock" size={18} className="text-primary mt-1" />
                <div>
                  <p className="font-medium text-accent-800">Time</p>
                  <p className="text-text-secondary">{formatTime(formData.time)}</p>
                </div>
              </div>
            )}
          </div>

          {/* Relationship & Significance */}
          <div className="space-y-4">
            <div className="flex items-start space-x-3">
              <Icon name="Users" size={18} className="text-primary mt-1" />
              <div>
                <p className="font-medium text-accent-800">Relationship</p>
                <p className="text-text-secondary">{getRelationshipLabel()}</p>
              </div>
            </div>

            {formData.significance && (
              <div className="flex items-start space-x-3">
                <Icon name="Star" size={18} className="text-primary mt-1" />
                <div>
                  <p className="font-medium text-accent-800">Significance</p>
                  <p className="text-text-secondary">{getSignificanceLabel()}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Additional Details */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Basic Details */}
        <div className="bg-white rounded-xl p-6 border border-accent-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-accent-800">Basic Details</h4>
            <Button
              variant="ghost"
              size="sm"
              iconName="Edit"
              onClick={() => onEdit(1)}
              className="text-accent-600"
            >
              Edit
            </Button>
          </div>
          
          <div className="space-y-3">
            <div>
              <p className="text-xs text-accent-600 uppercase font-medium">Occasion</p>
              <p className="text-accent-800">{getOccasionTypeLabel()}</p>
            </div>
            <div>
              <p className="text-xs text-accent-600 uppercase font-medium">Recipient</p>
              <p className="text-accent-800">{formData.recipient}</p>
            </div>
            <div>
              <p className="text-xs text-accent-600 uppercase font-medium">Date</p>
              <p className="text-accent-800">
                {formatDate(formData.date)}
                {formData.time && ` at ${formatTime(formData.time)}`}
              </p>
            </div>
          </div>
        </div>

        {/* Context Details */}
        <div className="bg-white rounded-xl p-6 border border-accent-200">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-semibold text-accent-800">Context & Preferences</h4>
            <Button
              variant="ghost"
              size="sm"
              iconName="Edit"
              onClick={() => onEdit(2)}
              className="text-accent-600"
            >
              Edit
            </Button>
          </div>
          
          <div className="space-y-3">
            <div>
              <p className="text-xs text-accent-600 uppercase font-medium">Relationship</p>
              <p className="text-accent-800">{getRelationshipLabel()}</p>
            </div>
            
            {formData.significance && (
              <div>
                <p className="text-xs text-accent-600 uppercase font-medium">Significance</p>
                <p className="text-accent-800">{getSignificanceLabel()}</p>
              </div>
            )}
            
            {formData.budgetRange && (
              <div>
                <p className="text-xs text-accent-600 uppercase font-medium">Budget</p>
                <p className="text-accent-800">{getBudgetLabel()}</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Additional Information */}
      {(formData.notes || formData.tags.length > 0) && (
        <div className="bg-white rounded-xl p-6 border border-accent-200">
          <h4 className="font-semibold text-accent-800 mb-4">Additional Information</h4>
          
          {formData.notes && (
            <div className="mb-4">
              <p className="text-xs text-accent-600 uppercase font-medium mb-2">Notes</p>
              <p className="text-accent-800 text-sm leading-relaxed">{formData.notes}</p>
            </div>
          )}
          
          {formData.tags.length > 0 && (
            <div>
              <p className="text-xs text-accent-600 uppercase font-medium mb-2">Style Preferences</p>
              <div className="flex flex-wrap gap-2">
                {formData.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-accent-100 text-accent-700 rounded-full text-sm"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Settings */}
      <div className="bg-white rounded-xl p-6 border border-accent-200">
        <h4 className="font-semibold text-accent-800 mb-4">Settings</h4>
        
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-accent-800">Reminders</p>
              <p className="text-sm text-text-secondary">{getReminderText()}</p>
            </div>
            <Icon 
              name={formData.reminderSettings.enabled ? "Bell" : "BellOff"} 
              size={18} 
              className={formData.reminderSettings.enabled ? "text-primary" : "text-accent-400"}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-accent-800">Recurring</p>
              <p className="text-sm text-text-secondary">
                {formData.isRecurring ? `Repeats ${formData.recurringPattern}` : 'One-time occasion'}
              </p>
            </div>
            <Icon 
              name={formData.isRecurring ? "RotateCcw" : "Calendar"} 
              size={18} 
              className={formData.isRecurring ? "text-primary" : "text-accent-400"}
            />
          </div>
          
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-accent-800">Calendar Sync</p>
              <p className="text-sm text-text-secondary">
                {formData.syncWithCalendar ? 'Will sync with calendar apps' : 'No calendar sync'}
              </p>
            </div>
            <Icon 
              name={formData.syncWithCalendar ? "Calendar" : "CalendarOff"} 
              size={18} 
              className={formData.syncWithCalendar ? "text-primary" : "text-accent-400"}
            />
          </div>
        </div>
      </div>

      {/* AI Recommendations Preview */}
      <div className="bg-gradient-to-br from-accent-50 to-primary-50 rounded-xl p-6 border border-accent-200">
        <div className="flex items-center space-x-3 mb-4">
          <div className="w-10 h-10 bg-gradient-to-br from-primary to-secondary rounded-lg flex items-center justify-center">
            <Icon name="Sparkles" size={20} color="white" />
          </div>
          <div>
            <h4 className="font-semibold text-accent-800">What's Next?</h4>
            <p className="text-sm text-text-secondary">After creating this occasion</p>
          </div>
        </div>
        
        <div className="space-y-3">
          <div className="flex items-center space-x-3">
            <div className="w-2 h-2 bg-primary rounded-full"></div>
            <p className="text-sm text-accent-700">
              Get personalized gift recommendations based on your occasion details
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-2 h-2 bg-secondary rounded-full"></div>
            <p className="text-sm text-accent-700">
              Receive smart reminders at the perfect time
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-2 h-2 bg-accent-600 rounded-full"></div>
            <p className="text-sm text-accent-700">
              Track your gift-giving history and build stronger relationships
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SummaryStep;